/**
 * Issue Detail Page - Detailed view of a specific data quality issue
 * 
 * Shows complete issue information including:
 * - Issue details with business impact
 * - Lineage context and affected objects
 * - Stakeholder notifications
 * - Resolution workflow
 * - New issue creation form
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShareIcon,
  CubeIcon,
  PencilIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import lineageApiService from '../../api/lineageApiService';
import enhancedApiService from '../../api/enhancedApiService';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { metadataIntegrationService } from '../../services/metadataIntegrationService';
import CommentThread from '../../components/issues/CommentThread';
import AssignmentDropdown from '../../components/issues/AssignmentDropdown';
import AssignmentSelector from '../../components/issues/AssignmentSelector';
import ActivityFeed from '../../components/issues/ActivityFeed';
import WatchButton from '../../components/issues/WatchButton';
import LinkedIssuesSection from '../../components/issues/LinkedIssuesSection';
import LinkIssueModal from '../../components/issues/LinkIssueModal';
import SimilarIssuesSection from '../../components/issues/SimilarIssuesSection';
import ImpactBadge from '../../components/issues/ImpactBadge';

const IssueDetailPage = () => {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const { showNotification, updateBreadcrumbs } = useUI();
  const { user } = useAuth();
  const [issue, setIssue] = useState(null);
  const [impactObjects, setImpactObjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [affectedStakeholders, setAffectedStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dataObjects, setDataObjects] = useState([]);
  const [isNewIssue, setIsNewIssue] = useState(false);
  const [assignedEntity, setAssignedEntity] = useState(null); // For new issue assignment
  const [watchers, setWatchers] = useState([]); // Issue watchers
  const [showLinkModal, setShowLinkModal] = useState(false); // Link issue modal
  const [linksRefreshTrigger, setLinksRefreshTrigger] = useState(0); // Trigger to refresh links
  const [tableMetadata, setTableMetadata] = useState(null); // Phase 3.8: Table metadata with tier status

  useEffect(() => {
    loadIssueDetails();
    if (issueId === 'new') {
      loadDataObjects();
    }
  }, [issueId]);

  // Load affected stakeholders whenever impactObjects changes
  useEffect(() => {
    if (impactObjects && impactObjects.length > 0 && !isNewIssue) {
      loadAffectedStakeholders();
    }
  }, [impactObjects, isNewIssue]);

  // Update breadcrumbs when issue changes
  useEffect(() => {
    if (issue && issue.title && !isNewIssue) {
      updateBreadcrumbs([
        { name: 'Issues', href: '/issues' },
        { name: issue.title }
      ]);
    } else if (isNewIssue) {
      updateBreadcrumbs([
        { name: 'Issues', href: '/issues' },
        { name: 'New Issue' }
      ]);
    }

    // Cleanup: clear breadcrumbs when component unmounts
    return () => {
      updateBreadcrumbs([]);
    };
  }, [issue, isNewIssue, updateBreadcrumbs]);

  const loadDataObjects = async () => {
    try {
      const response = await enhancedApiService({
        method: 'GET',
        url: '/data-objects'
      });
      if (response.success) {
        setDataObjects(response.data_objects);
      }
    } catch (error) {
      console.error('Error loading data objects:', error);
    }
  };

  // Phase 3.8: Helper to parse database.schema.table from full table name
  const parseFullTableName = (fullName) => {
    if (!fullName) return { database: null, schema: null, table: null };
    const parts = fullName.split('.');

    // Standard format: DATABASE.SCHEMA.TABLE (3 parts)
    if (parts.length === 3) {
      return {
        database: parts[0],
        schema: parts[1],
        table: parts[2]
      };
    }

    // Fallback for legacy 2-part format: SCHEMA.TABLE
    if (parts.length === 2) {
      return {
        database: null,
        schema: parts[0],
        table: parts[1]
      };
    }

    return { database: null, schema: null, table: null };
  };

  // Phase 3.8: Load table metadata for tier status
  const loadTableMetadata = async (sourceObject) => {
    if (!sourceObject || !sourceObject.connection_id || !sourceObject.full_name) {
      return;
    }

    try {
      const { database, schema, table } = parseFullTableName(sourceObject.full_name);
      if (!database || !schema || !table) return;

      const response = await metadataIntegrationService.getTableMetadataTiered(
        sourceObject.connection_id,
        { database, schema },
        {}
      );

      if (response && response.success) {
        // Find the specific table
        const tableMatch = response.tables?.find(
          t => (t.table_catalog || t.database_name) === database &&
               t.table_schema === schema &&
               t.table_name === table
        );

        if (tableMatch) {
          setTableMetadata({
            tierStatus: response.tierStatus,
            database: database,
            schema: schema,
            tableName: table,
            tier1Complete: tableMatch.tier1_collected_at != null,
            tier2Complete: tableMatch.tier2_collected_at != null,
            tier3Complete: tableMatch.tier3_collected_at != null,
            tier4Complete: tableMatch.tier4_collected_at != null
          });
        }
      }
    } catch (error) {
      console.error('Error loading table metadata:', error);
    }
  };

  // Phase 3.8: Get tier status badge
  const getTierStatusBadge = () => {
    if (!tableMetadata) return null;

    // Count completed tiers
    const completedTiers = [
      tableMetadata.tier1Complete,
      tableMetadata.tier2Complete,
      tableMetadata.tier3Complete,
      tableMetadata.tier4Complete
    ].filter(Boolean).length;

    if (completedTiers === 0) return null;

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Tier {completedTiers}/4 Complete
      </span>
    );
  };

  const loadAffectedStakeholders = async () => {
    try {
      // Get unique object IDs from impactObjects
      const objectIds = [...new Set(impactObjects.map(obj => obj.data_object_id || obj.impacted_object_id))].filter(Boolean);

      if (objectIds.length === 0) return;

      // Fetch stakeholder assignments for all affected objects
      const stakeholderPromises = objectIds.map(objectId =>
        lineageApiService.getObjectStakeholders(objectId).catch(() => ({ data: [] }))
      );

      const stakeholderResults = await Promise.all(stakeholderPromises);

      // Flatten and deduplicate stakeholder groups
      const allStakeholders = stakeholderResults.flatMap(result => result.data || result || []);
      const uniqueStakeholders = Array.from(
        new Map(allStakeholders.map(item => [item.stakeholder_group?.id || item.stakeholder_group_id, item])).values()
      );

      setAffectedStakeholders(uniqueStakeholders);
    } catch (error) {
      console.error('Error loading affected stakeholders:', error);
    }
  };

  const loadIssueDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Handle "new" issue creation case
      if (issueId === 'new') {
        setIsNewIssue(true);
        // Set up empty issue for creation
        setIssue({
          id: null,
          title: '',
          description: '',
          severity: 'medium',
          status: 'open',
          issue_type: 'data_quality',
          source_object_id: '',
          priority_score: 50,
          estimated_user_impact: 0,
          business_processes_affected: [],
          business_impact_summary: ''
        });
        setAssignedEntity(null);
        setImpactObjects([]);
        setNotifications([]);
        setLoading(false);
        return;
      }
      
      const response = await lineageApiService.getIssue(issueId);

      // Backend returns the issue object directly
      if (response && !response.error) {
        setIssue(response);
        setImpactObjects(response.impact_objects || []);
        setNotifications(response.notifications || []);

        // Phase 3.8: Load table metadata for tier status
        if (response.source_object) {
          await loadTableMetadata(response.source_object);
        }
      } else {
        setError(response.error || 'Failed to load issue details');
      }
    } catch (err) {
      console.error('Error loading issue details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIssue = async () => {
    if (!issue.title.trim()) {
      showNotification('Please provide a title for the issue', 'error');
      return;
    }
    if (!issue.description.trim()) {
      showNotification('Please provide a description for the issue', 'error');
      return;
    }
    if (!issue.source_object_id) {
      showNotification('Please select a source data object', 'error');
      return;
    }

    try {
      setSaving(true);

      // Prepare assignment data based on selected entity
      const assignmentData = {};
      if (assignedEntity) {
        if (assignedEntity.type === 'user') {
          assignmentData.assigned_to = assignedEntity.id;
          assignmentData.assigned_team = null;
          assignmentData.assigned_to_team_id = null;
        } else if (assignedEntity.type === 'team') {
          assignmentData.assigned_to = null;
          assignmentData.assigned_team = assignedEntity.name;
          assignmentData.assigned_to_team_id = assignedEntity.id;
        }
      }

      const response = await enhancedApiService({
        method: 'POST',
        url: '/issues',
        data: {
          title: issue.title,
          description: issue.description,
          severity: issue.severity,
          issue_type: issue.issue_type,
          source_object_id: issue.source_object_id,
          ...assignmentData
        }
      });
      
      if (response.success) {
        showNotification('Issue created successfully!', 'success');
        navigate('/issues');
      } else {
        showNotification(`Failed to create issue: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Error creating issue:', error);
      showNotification(`Failed to create issue: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResolveIssue = async () => {
    if (!resolutionNotes.trim()) {
      showNotification('Please provide resolution notes', 'error');
      return;
    }

    try {
      setResolving(true);
      
      const response = await lineageApiService.resolveIssue(issueId, {
        resolution_notes: resolutionNotes,
        resolution_method: 'manual_fix'
      });
      
      if (response.success) {
        await loadIssueDetails(); // Reload to show updated status
        setResolutionNotes('');
        showNotification('Issue resolved successfully!', 'success');
      } else {
        showNotification(`Failed to resolve issue: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Error resolving issue:', error);
      showNotification(`Failed to resolve issue: ${error.message}`, 'error');
    } finally {
      setResolving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setIssue(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />;
      case 'high': return <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />;
      case 'medium': return <ClockIcon className="h-6 w-6 text-yellow-500" />;
      case 'low': return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      default: return <ClockIcon className="h-6 w-6 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { bg: 'bg-red-100', text: 'text-red-800', label: 'Open' },
      investigating: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Investigating' },
      resolved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Resolved' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
      false_positive: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'False Positive' }
    };

    const config = statusConfig[status] || statusConfig.open;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getImpactTypeIcon = (impactType) => {
    switch (impactType) {
      case 'source': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'downstream_direct': return <ShareIcon className="h-5 w-5 text-orange-500" />;
      case 'downstream_indirect': return <ShareIcon className="h-5 w-5 text-yellow-500" />;
      default: return <CubeIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading issue details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          <h2 className="text-lg font-semibold text-red-800">Error Loading Issue</h2>
        </div>
        <p className="text-red-700 mt-2">{error}</p>
        <button
          onClick={() => navigate('/issues')}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Issues
        </button>
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Issue not found</h3>
        <button
          onClick={() => navigate('/issues')}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Issues
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/issues')}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>

        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            {getSeverityIcon(issue.severity)}
            <h1 className="text-2xl font-bold text-gray-900">
              {isNewIssue ? 'Create New Issue' : issue.title}
            </h1>
            {!isNewIssue && getStatusBadge(issue.status)}
          </div>
          <p className="text-gray-600">
            {issue.id ? `Issue #${issue.id.split('-')[0]} • Created ${new Date(issue.created_at).toLocaleDateString()}` : 'New Issue'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          {/* Link Issue Button for Existing Issues */}
          {!isNewIssue && (
            <button
              onClick={() => setShowLinkModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <LinkIcon className="h-4 w-4" />
              Link Issue
            </button>
          )}

          {/* Watch Button for Existing Issues */}
          {!isNewIssue && (
            <WatchButton
              issueId={issueId}
              onWatchersChange={setWatchers}
            />
          )}

          {/* Save Button for New Issues */}
          {isNewIssue && (
            <button
              onClick={handleSaveIssue}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              {saving ? 'Creating...' : 'Create Issue'}
            </button>
          )}
        </div>
      </div>

      {/* Issue Metadata Bar - Horizontal */}
      {!isNewIssue && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between gap-6">
            {/* Left side metadata */}
            <div className="flex items-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Priority:</span>
                <span className="font-medium">{issue.priority_score?.toFixed(1) || 0}/100</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-gray-600">Created:</span>
                <span className="font-medium">{new Date(issue.created_at).toLocaleDateString()}</span>
              </div>

              {/* Business Impact Badges */}
              {issue.business_impact_badges && (
                <div className="flex items-center">
                  <ImpactBadge
                    impactData={issue.business_impact_badges}
                    size="md"
                    layout="horizontal"
                    showTooltip={true}
                  />
                </div>
              )}

              {/* Watchers */}
              {watchers.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Watchers:</span>
                  <div className="flex items-center -space-x-2">
                    {watchers.slice(0, 3).map((watcher) => (
                      <div
                        key={watcher.id}
                        className="relative inline-block"
                        title={`${watcher.user?.first_name || ''} ${watcher.user?.last_name || ''} (${watcher.user?.email || ''})`}
                      >
                        {watcher.user?.avatar_url ? (
                          <img
                            src={watcher.user.avatar_url}
                            alt={watcher.user.first_name}
                            className="h-8 w-8 rounded-full border-2 border-white bg-gray-200"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full border-2 border-white bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-700">
                            {(watcher.user?.first_name?.[0] || watcher.user?.email?.[0] || '?').toUpperCase()}
                          </div>
                        )}
                      </div>
                    ))}
                    {watchers.length > 3 && (
                      <div className="h-8 w-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                        +{watchers.length - 3}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right side - Assignment */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Assigned to:</span>
              <AssignmentDropdown
                issue={issue}
                onAssignmentChange={(updatedIssue) => setIssue(updatedIssue)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Full Width */}
      <div className="space-y-6">
          {/* Issue Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Issue Details</h2>
            
            <div className="space-y-4">
              {/* Title Field */}
              <div>
                <label className="text-sm font-medium text-gray-700">Title *</label>
                {isNewIssue ? (
                  <input
                    type="text"
                    value={issue.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter issue title"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-900">{issue.title}</p>
                )}
              </div>

              {/* Description Field */}
              <div>
                <label className="text-sm font-medium text-gray-700">Description *</label>
                {isNewIssue ? (
                  <textarea
                    value={issue.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the issue in detail"
                  />
                ) : (
                  <div className="mt-1 prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm text-gray-900">{issue.description}</pre>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Issue Type */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  {isNewIssue ? (
                    <select
                      value={issue.issue_type}
                      onChange={(e) => handleInputChange('issue_type', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="data_quality">Data Quality</option>
                      <option value="schema_change">Schema Change</option>
                      <option value="pipeline_failure">Pipeline Failure</option>
                      <option value="validation_failure">Validation Failure</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 capitalize">{issue.issue_type.replace('_', ' ')}</p>
                  )}
                </div>

                {/* Severity */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Severity</label>
                  {isNewIssue ? (
                    <select
                      value={issue.severity}
                      onChange={(e) => handleInputChange('severity', e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 capitalize">{issue.severity}</p>
                  )}
                </div>
              </div>
              
              {/* Source Object */}
              <div>
                <label className="text-sm font-medium text-gray-700">Source Object *</label>
                {isNewIssue ? (
                  <select
                    value={issue.source_object_id}
                    onChange={(e) => handleInputChange('source_object_id', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a data object</option>
                    {dataObjects.map((obj) => (
                      <option key={obj.id} value={obj.id}>
                        {obj.full_name} ({obj.object_type})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-sm text-gray-900">{issue.source_object?.full_name || 'Unknown'}</p>
                    {/* Phase 3.8: Show tier status */}
                    {getTierStatusBadge()}
                  </div>
                )}
              </div>

              {/* Assignment Field for New Issues */}
              {isNewIssue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assign To (Optional)
                  </label>
                  <AssignmentSelector
                    value={assignedEntity}
                    onChange={setAssignedEntity}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Search and select a user or team to assign this issue to
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Business Impact - Only show for existing issues */}
          {!isNewIssue && issue.business_impact_badges && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5" />
                Business Impact
              </h2>
              {issue.business_impact_summary && (
                <p className="text-blue-800 mb-4">{issue.business_impact_summary}</p>
              )}

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-blue-700">Teams Affected</label>
                  <p className="text-lg font-semibold text-blue-900">{issue.business_impact_badges.teams_affected || 0}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-blue-700">Users Affected</label>
                  <p className="text-lg font-semibold text-blue-900">{issue.business_impact_badges.users_affected || 0}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-blue-700">Priority Score</label>
                  <p className="text-lg font-semibold text-blue-900">{issue.priority_score?.toFixed(1) || 0}/100</p>
                </div>
              </div>
              
              {issue.business_processes_affected?.length > 0 && (
                <div className="mt-4">
                  <label className="text-sm font-medium text-blue-700">Affected Processes</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {issue.business_processes_affected.map((process, index) => (
                      <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {process}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Impacted Objects - Only show for existing issues */}
          {!isNewIssue && impactObjects.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShareIcon className="h-5 w-5" />
                Impacted Objects ({impactObjects.length})
              </h2>
              
              <div className="space-y-3">
                {impactObjects.map((impact) => (
                  <div key={impact.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getImpactTypeIcon(impact.impact_type)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {impact.impacted_object?.full_name || 'Unknown Object'}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {impact.impact_type.replace('_', ' ')} • {impact.impact_level} impact
                        </p>
                      </div>
                    </div>
                    
                    {impact.impact_path?.length > 0 && (
                      <div className="text-xs text-gray-500">
                        Path: {impact.impact_path.length} steps
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolution Section - Only for existing open issues */}
          {!isNewIssue && issue.status === 'open' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PencilIcon className="h-5 w-5" />
                Resolve Issue
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Resolution Notes</label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    rows={4}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe how this issue was resolved..."
                  />
                </div>
                
                <button
                  onClick={handleResolveIssue}
                  disabled={resolving || !resolutionNotes.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  {resolving ? 'Resolving...' : 'Resolve Issue'}
                </button>
              </div>
            </div>
          )}

          {/* Resolution Details - Only for resolved issues */}
          {!isNewIssue && issue.status === 'resolved' && issue.resolution_notes && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5" />
                Resolution
              </h2>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-green-700">Resolved By</label>
                  <p className="text-sm text-green-800">{issue.resolved_by || 'Unknown'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-green-700">Resolved At</label>
                  <p className="text-sm text-green-800">
                    {new Date(issue.resolved_at).toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-green-700">Resolution Notes</label>
                  <p className="text-sm text-green-800">{issue.resolution_notes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Comments Section - Only for existing issues */}
          {!isNewIssue && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <CommentThread issueId={issueId} currentUser={user} />
            </div>
          )}

          {/* Activity Feed - Only for existing issues */}
          {!isNewIssue && (
            <ActivityFeed issueId={issueId} />
          )}

          {/* Linked Issues - Only for existing issues */}
          {!isNewIssue && (
            <LinkedIssuesSection
              issueId={issueId}
              refreshTrigger={linksRefreshTrigger}
              onLinkDeleted={() => {
                // Refresh links when deleted
                setLinksRefreshTrigger(prev => prev + 1);
              }}
            />
          )}

          {/* Similar Issues - Only for existing issues */}
          {!isNewIssue && (
            <SimilarIssuesSection issueId={issueId} />
          )}

          {/* Affected Stakeholders - Only for existing issues */}
          {!isNewIssue && affectedStakeholders.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-purple-600" />
                Affected Stakeholders ({affectedStakeholders.length})
              </h3>

              <div className="space-y-3">
                {affectedStakeholders.map((stakeholder) => {
                  const group = stakeholder.stakeholder_group || stakeholder;
                  return (
                    <div key={stakeholder.id || stakeholder.stakeholder_group_id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <UserGroupIcon className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {group.group_name || 'Unknown Group'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-600 capitalize">
                              {stakeholder.relationship_type || 'consumer'}
                            </span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              stakeholder.business_impact_level === 'critical' ? 'bg-red-100 text-red-800' :
                              stakeholder.business_impact_level === 'high' ? 'bg-orange-100 text-orange-800' :
                              stakeholder.business_impact_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {stakeholder.business_impact_level || 'low'} impact
                            </span>
                          </div>
                          {stakeholder.impact_description && (
                            <p className="text-xs text-gray-500 mt-1">
                              {stakeholder.impact_description}
                            </p>
                          )}
                        </div>
                      </div>

                      <a
                        href={`/admin/stakeholder-groups/${group.id || stakeholder.stakeholder_group_id}`}
                        className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View Group →
                      </a>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> These stakeholder groups are assigned to the data objects affected by this issue.
                  They will receive notifications based on their configured notification preferences.
                </p>
              </div>
            </div>
          )}

          {/* Notifications - Only for existing issues */}
          {!isNewIssue && notifications.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                Notifications ({notifications.length})
              </h3>

              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div key={notification.id} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">
                        {notification.stakeholder_group?.group_name || 'Unknown Group'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        notification.delivery_status === 'sent' ? 'bg-green-100 text-green-800' :
                        notification.delivery_status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {notification.delivery_status}
                      </span>
                    </div>
                    <p className="text-gray-600 capitalize">
                      {notification.notification_type} via {notification.notification_channel}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      {/* Link Issue Modal */}
      <LinkIssueModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        currentIssueId={issueId}
        onLinkCreated={() => {
          // Refresh the linked issues section
          setLinksRefreshTrigger(prev => prev + 1);
        }}
      />
    </div>
  );
};

export default IssueDetailPage;